import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "contracts";

export function useContractList(options?: {
	companyId?: string | undefined;
	status?: string | undefined;
	type?: string | undefined;
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.companyId) params.set("companyId", options.companyId);
			if (options?.status) params.set("status", options.status);
			if (options?.type) params.set("type", options.type);
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			return api.get<{ items: Contract[]; total: number }>(`/api/contracts?${params.toString()}`);
		},
	});
}

export function useContractDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Contract>(`/api/contracts/${id}`),
		enabled: !!id,
	});
}

export function useCreateContract() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateContractInput) => api.post<Contract>("/api/contracts", data),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useUpdateContract() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CreateContractInput> }) =>
			api.patch<Contract>(`/api/contracts/${id}`, data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useContractClauseDefaults() {
	return useQuery({
		queryKey: [KEY, "clause-defaults"],
		queryFn: () => api.get<Record<string, string>>("/api/contracts/clause-defaults"),
		staleTime: 5 * 60 * 1000,
	});
}

export const CONTRACT_CLAUSE_FIELDS: { key: string; label: string }[] = [
	{ key: "contract_clause_2", label: "2. Madde - Teslimat" },
	{ key: "contract_clause_3", label: "3. Madde - Teslimat Şekli" },
	{ key: "contract_clause_4", label: "4. Madde - Montaj" },
	{ key: "contract_bank_info", label: "6. Madde - Banka Bilgileri" },
	{ key: "contract_payment_other", label: "6. Madde - Ödeme İle İlgili Diğer Hususlar" },
	{ key: "contract_start_operation", label: "6. Madde - Start İşlemi" },
	{ key: "contract_clause_7", label: "7. Madde - Garanti" },
	{ key: "contract_clause_8", label: "8. Madde - Keşifsiz Satış Sorumluluğu" },
	{ key: "contract_clause_9", label: "9. Madde - Temerrüt" },
	{ key: "contract_clause_10", label: "10. Madde - Resmi İzin/Vergi" },
	{ key: "contract_clause_11", label: "11. Madde - Cayma Cezai Şartı" },
	{ key: "contract_clause_12", label: "12. Madde - İSG Sorumluluğu" },
	{ key: "contract_clause_13", label: "13. Madde - Ticari Belgeler/Referans" },
	{ key: "contract_clause_14", label: "14. Madde - KVKK" },
	{ key: "contract_clause_15", label: "15. Madde - Delil Sözleşmesi (HMK 193)" },
	{ key: "contract_clause_16", label: "16. Madde - Yetkili Mahkeme" },
];

export function useDeleteContract() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/contracts/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export interface ContractEmailPreview {
	email: string;
	cc: string;
	subject: string;
	message: string;
	hasCustomTemplate: boolean;
	recipientName: string;
	documentNumber: string;
	defaultLang: "en" | "tr";
	signatureMissingReason: string | null;
}

export function useContractEmailPreview() {
	return useMutation({
		mutationFn: (id: string) =>
			api.get<ContractEmailPreview>(`/api/contracts/${id}/email-preview`),
	});
}

export function useSendContractEmail() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			...body
		}: {
			id: string;
			email: string;
			cc?: string;
			subject: string;
			message: string;
			lang: "en" | "tr";
		}) => api.post<{ sent: boolean }>(`/api/contracts/${id}/send-email`, body),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useSaveContractMailTemplate() {
	return useMutation({
		mutationFn: (input: { subject: string | null; body: string | null }) =>
			api.patch("/auth/contract-mail-template", input),
	});
}

interface Contract {
	id: string;
	documentNumber: string | null;
	title: string;
	type: "sales" | "maintenance" | "rental" | "service";
	startDate: string | null;
	endDate: string | null;
	value: string | null;
	currency: string | null;
	autoRenewal: boolean;
	renewalNoticeDays: number | null;
	terms: string | null;
	notes: string | null;
	status: "draft" | "active" | "expired" | "terminated" | "renewed";
	signedAt: string | null;
	signedBy: string | null;
	companyId: string | null;
	opportunityId: string | null;
	orderId: string | null;
	offerId: string | null;
	assignedTo: string | null;
	teamId: string | null;
	visibility: string;
	companyName: string | null;
	clauseTexts: Record<string, string> | null;
	emailSentAt: string | null;
	createdAt: string;
	updatedAt: string;
}

interface CreateContractInput {
	title: string;
	type?: "sales" | "maintenance" | "rental" | "service";
	status?: "draft" | "active" | "expired" | "terminated" | "renewed";
	startDate?: string | undefined;
	endDate?: string | undefined;
	value?: number | undefined;
	currency?: string | undefined;
	autoRenewal?: boolean;
	renewalNoticeDays?: number | undefined;
	terms?: string | undefined;
	notes?: string | undefined;
	companyId?: string | undefined;
	opportunityId?: string | undefined;
	orderId?: string | undefined;
	offerId?: string | undefined;
	assignedTo?: string | undefined;
	clauseTexts?: Record<string, string> | undefined;
}
